import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Header from "./Header";
import { getAuthHeader } from "./utils";
import { useNavigate } from "react-router-dom";

const UnifiedDashboard = () => {
  const [items, setItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [counts, setCounts] = useState({
    total: 0,
    companies: 0,
    services: 0,
    tickets: 0,
  });
  const [activeFilter, setActiveFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const limit = 10;

  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: limit.toString(),
        ...(searchTerm && { search: searchTerm }),
        ...(activeFilter && { type: activeFilter }),
      });

      const response = await axios.get(
        `https://api.instacorp.ae/api/admin/data/aggregate-data?${queryParams}`,
        { headers: getAuthHeader() }
      );

      if (response.data.success) {
        console.log(response.data);
        setItems(response.data.data);
        setTotalPages(response.data.pagination.pages);
        setCounts(response.data.counts);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setIsLoading(false);
  }, [currentPage, activeFilter, searchTerm]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchData();
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [fetchData]);

  const getTypeIndicator = (type) => {
    const types = {
      company: {
        bg: "bg-purple-100",
        text: "text-purple-800",
        label: "Company",
      },
      service: {
        bg: "bg-blue-100",
        text: "text-blue-800",
        label: "Service",
      },
      ticket: {
        bg: "bg-green-100",
        text: "text-green-800",
        label: "Ticket",
      },
    };
    return types[type] || types.company;
  };

  const getStatusBadge = (status) => {
    const statuses = {
      completed: "bg-green-100 text-green-800",
      processing: "bg-yellow-100 text-yellow-800",
      pending: "bg-blue-100 text-blue-800",
      live: "bg-purple-100 text-purple-800",
      default: "bg-gray-100 text-gray-600",
      imported: "bg-orange-100 text-orange-600",
    };
    return statuses[status?.toLowerCase()] || statuses.default;
  };

  const handleItemClick = (item) => {
    switch (item.type) {
      case "company":
        navigate(`/companies/${item._id}`);
        break;
      case "service":
        if (item.companyId) {
          navigate(`/companies/${item.companyId}/services/${item._id}`);
        } else {
          navigate(`/services/${item._id}`);
        }
        break;
      case "ticket":
        navigate(`/tickets/${item._id}`);
        break;
      default:
        break;
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /** Safely extract a displayable string from a value that might be a plain string or an object with a `name` key */
  const safeStr = (val) => {
    if (val == null) return "";
    if (typeof val === "string") return val;
    if (typeof val === "object" && val.name) return val.name;
    return String(val);
  };

  const getItemTitle = (item) => {
    switch (item.type) {
      case "company":
        return safeStr(item.title) || "Untitled Company";
      case "service":
        return `${safeStr(item.title)} - ${safeStr(item.companyName) || "N/A"}`;
      case "ticket":
        return `${safeStr(item.title)} - ${safeStr(item.ticketType) || "N/A"}`;
      default:
        return "N/A";
    }
  };

  return (
    <div>
      <Header />
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold text-gray-900">
              Activity Dashboard
            </h1>
          </div>
          <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
            <div className="flex items-center space-x-4">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-4 py-2 border rounded-lg w-64 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px space-x-8 px-4" aria-label="Tabs">
              <button
                onClick={() => setActiveFilter("")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeFilter === ""
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                All ({counts.total})
              </button>
              <button
                onClick={() => setActiveFilter("companies")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeFilter === "companies"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Companies ({counts.companies})
              </button>
              <button
                onClick={() => setActiveFilter("services")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeFilter === "services"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Services ({counts.services})
              </button>
              <button
                onClick={() => setActiveFilter("tickets")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeFilter === "tickets"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Tickets ({counts.tickets})
              </button>
            </nav>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Updated
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                      </div>
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-4 text-center text-gray-500"
                    >
                      No items found
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr
                      key={`${item.type}-${item._id}`}
                      onClick={() => handleItemClick(item)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            getTypeIndicator(item.type).bg
                          } ${getTypeIndicator(item.type).text}`}
                        >
                          {getTypeIndicator(item.type).label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {getItemTitle(item)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(
                            item.status
                          )}`}
                        >
                          {item.status || "N/A"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {item.userDetails ? (
                          <div className="text-sm">
                            <div className="font-medium text-gray-900">
                              {`${item.userDetails.first_name} ${
                                item.userDetails.last_name || ""
                              }`}
                            </div>
                            <div className="text-gray-500">
                              {item.userDetails.email}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-500">N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(item.lastUpdated)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
          <div className="flex justify-between flex-1 sm:hidden">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="relative inline-flex items-center px-4 py-2 ml-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing page <span className="font-medium">{currentPage}</span>{" "}
                of <span className="font-medium">{totalPages}</span>
              </p>
            </div>
            <div>
              <nav
                className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                aria-label="Pagination"
              >
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === pageNum
                          ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                          : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedDashboard;
